/**
 * Self-service client wrapper for `/api/me` — Phase A.2 of
 * `ai-docs/lexiun-localization-plan.md`.
 *
 * Scoped to the authenticated user's record. The body never carries a
 * target userId; the server resolves identity from the JWT `sub`. The
 * lexserver route is mounted at `/api/me` (see
 * `lexserver/src/routes/me.ts`).
 *
 * Kept tiny + self-contained — does NOT import `lexserver-client.ts`'s
 * case fetchers so a SettingsLayout that only needs to PATCH locale
 * doesn't pay the cost of loading the wider client surface.
 */

import { getToken } from "@/lib/lexiun-jwt";
import { newTraceparent } from "@/lib/traceparent";
import type { LanguagePreference } from "@/lib/language";

const ENDPOINT = "/api/me";

export interface PatchMePayload {
  locale?: LanguagePreference;
  displayName?: string;
}

/**
 * Curated org payload returned alongside the user by `/api/me`
 * (lexserver-side `pickOrgPayload` in routes/me.ts; added 2026-05-20).
 * Identity + locale/region + tier — useful for the Header chrome (org
 * name in front of the Lexiun wordmark), Settings, and any UI surface
 * that needs the human org name without a second round-trip.
 *
 * `null` when the user's org doc isn't found (legacy/orphaned — rare).
 *
 * **Type-width tradeoff:** `orgType` / `status` / `jurisdiction` /
 * `timezone` / `currency` / `tier` are typed as plain `string` rather
 * than as literal unions matching the server enums (`OrgType`,
 * `OrgStatus`, etc.). The server-side enums live in
 * `lexserver/src/db/models/organization.ts` and aren't shared with
 * lexide (no cross-package type contract today). Widening loses some
 * type-safety at the consumer; tightening would require either a
 * shared types package or duplicating each enum here. Pragmatic for
 * now; the Header (the only consumer) only reads `name`, so the
 * widened fields don't bite.
 */
export interface MeOrgPayload {
  orgId: string;
  name: string;
  slug: string;
  orgType: string;
  status: string;
  locale: LanguagePreference;
  jurisdiction: string;
  timezone: string;
  currency: string;
  tier: string;
}

/** Shape of the server response data. Mirrors the lexserver User model
 *  but only the fields the SPA actually consumes. */
export interface MeResponse {
  userId: string;
  orgId: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  role: "MEMBER" | "ORG_ADMIN";
  locale?: LanguagePreference;
  /** Curated org payload (added 2026-05-20 — see `MeOrgPayload`). */
  org: MeOrgPayload | null;
}

/**
 * Surface a typed error when the PATCH fails. Carries the server's
 * `code` so callers can branch (e.g. `INVALID_LOCALE` vs network blip)
 * and `status` so the rollback handler can distinguish 4xx (won't
 * retry — payload is bad) from 5xx (transient — could prompt to retry).
 */
export class PatchMeError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "PatchMeError";
    this.status = status;
    this.code = code;
  }
}

async function authedHeaders(): Promise<Record<string, string>> {
  const token = await getToken();
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    traceparent: newTraceparent(),
  };
}

/** PATCH /api/me — mutate the authenticated user's locale / displayName.
 *  Returns the full updated User. Throws `PatchMeError` on non-2xx. */
export async function patchMe(payload: PatchMePayload): Promise<MeResponse> {
  const headers = await authedHeaders();
  const res = await fetch(ENDPOINT, {
    method: "PATCH",
    headers,
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: { code?: string; message?: string };
    };
    throw new PatchMeError(
      body.error?.message ?? `PATCH /api/me failed: ${res.status}`,
      res.status,
      body.error?.code,
    );
  }

  const body = (await res.json()) as { data: MeResponse };
  // Keep the fetchMe() session cache coherent: a successful PATCH is the
  // freshest possible view of the record. Resolve any in-flight cache to it.
  meCache = Promise.resolve(body.data);
  return body.data;
}

/**
 * `fetchMe()` GET `/api/me` + a module-level session cache.
 *
 * The lexserver `/api/me` endpoint resolves identity from the JWT `sub`, so
 * the response is stable for the session. Caching at module scope dedupes
 * repeated lookups across the LexIDE app. Cleared on sign-out via
 * `clearMeCache()`.
 *
 * The cache stores the in-flight Promise (not just the resolved value) so
 * concurrent first callers share one network round-trip. A rejected fetch is
 * NOT cached — the next call retries.
 */
let meCache: Promise<MeResponse> | null = null;

export async function fetchMe(): Promise<MeResponse> {
  if (meCache) return meCache;
  const pending = (async () => {
    const headers = await authedHeaders();
    const res = await fetch(ENDPOINT, { method: "GET", headers });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as {
        error?: { code?: string; message?: string };
      };
      throw new PatchMeError(
        body.error?.message ?? `GET /api/me failed: ${res.status}`,
        res.status,
        body.error?.code,
      );
    }
    const body = (await res.json()) as { data: MeResponse };
    return body.data;
  })();
  meCache = pending;
  try {
    return await pending;
  } catch (err) {
    // Don't poison the cache with a transient failure.
    if (meCache === pending) meCache = null;
    throw err;
  }
}

/** Drop the cached `/api/me` view (sign-out / identity switch / tests). */
export function clearMeCache(): void {
  meCache = null;
}

/**
 * Resolve a non-empty display name for greetings. Chain:
 * `displayName → firstName → email-local-part → caller's localized generic`.
 * The generic is passed in so this helper stays pure + i18n-free (callers
 * supply the localized string via react-i18next).
 */
export function resolveDisplayName(
  me: Pick<MeResponse, "displayName" | "firstName" | "email">,
  genericFallback = "there",
): string {
  const candidates = [
    me.displayName,
    me.firstName,
    me.email?.includes("@") ? me.email.split("@")[0] : "",
  ];
  for (const c of candidates) {
    const trimmed = c?.trim();
    if (trimmed) return trimmed;
  }
  return genericFallback;
}
